/**
 * Money.
 *
 * Money is transported as a decimal *string* ("1234.50"), never a JavaScript
 * number. `0.1 + 0.2 !== 0.3` is a rounding curiosity in most software and a
 * defect in a system that commits school budgets and matches invoices to the
 * penny.
 *
 * Rules:
 *   · PostgreSQL numeric(14,2) is the source of truth for all arithmetic.
 *   · The API returns strings; the UI formats them for display.
 *   · Client-side arithmetic uses the integer-pence helpers below, and only for
 *     provisional totals shown while typing. The server always recalculates.
 */

export type Money = string;

const SCALE = 2;

/** Parse a decimal string to integer minor units (pence). Throws on nonsense. */
export function toPence(value: Money | number): number {
  const text = typeof value === 'number' ? value.toFixed(SCALE) : value.trim();
  if (!/^-?\d+(\.\d+)?$/.test(text)) {
    throw new Error(`Not a valid monetary amount: ${JSON.stringify(value)}`);
  }
  const negative = text.startsWith('-');
  const [whole = '0', fraction = ''] = text.replace('-', '').split('.');
  const pence =
    BigInt(whole) * 100n + BigInt((fraction + '00').slice(0, SCALE).padEnd(SCALE, '0'));
  const result = Number(pence);
  if (!Number.isSafeInteger(result)) {
    throw new Error(`Monetary amount out of safe range: ${value}`);
  }
  return negative ? -result : result;
}

/** Integer pence back to a decimal string. */
export function fromPence(pence: number): Money {
  const negative = pence < 0;
  const abs = Math.abs(Math.round(pence));
  const text = `${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
  return negative ? `-${text}` : text;
}

export function addMoney(...values: Money[]): Money {
  return fromPence(values.reduce((sum, v) => sum + toPence(v), 0));
}

export function subtractMoney(a: Money, b: Money): Money {
  return fromPence(toPence(a) - toPence(b));
}

/**
 * quantity × unitPrice. Quantity may be fractional (numeric(12,3)), so this
 * rounds half-up at the end — matching PostgreSQL's numeric rounding, so a
 * total computed here agrees with the same total computed by the database.
 */
export function multiplyMoney(unitPrice: Money, quantity: number | string): Money {
  const qty = typeof quantity === 'string' ? Number(quantity) : quantity;
  if (!Number.isFinite(qty)) throw new Error(`Not a valid quantity: ${quantity}`);
  return fromPence(Math.round(toPence(unitPrice) * qty));
}

/** VAT at a percentage rate, rounded half-up to the penny. */
export function vatOf(netAmount: Money, ratePercent: number | string): Money {
  const rate = typeof ratePercent === 'string' ? Number(ratePercent) : ratePercent;
  if (!Number.isFinite(rate)) throw new Error(`Not a valid VAT rate: ${ratePercent}`);
  return fromPence(Math.round((toPence(netAmount) * rate) / 100));
}

export function compareMoney(a: Money, b: Money): -1 | 0 | 1 {
  const [x, y] = [toPence(a), toPence(b)];
  return x < y ? -1 : x > y ? 1 : 0;
}

export const isZero = (v: Money): boolean => toPence(v) === 0;
export const isNegative = (v: Money): boolean => toPence(v) < 0;

export const ZERO: Money = '0.00';

/** "£1,234.50". Uses the browser's locale formatting for the number part. */
export function formatMoney(
  value: Money | null | undefined,
  options: { currency?: string; showZero?: boolean; abbreviate?: boolean } = {},
): string {
  const { currency = 'GBP', showZero = true, abbreviate = false } = options;
  if (value === null || value === undefined || value === '') return '—';
  const pence = toPence(value);
  if (pence === 0 && !showZero) return '—';

  // Dashboards get "£1.2m" / "£45.6k"; everything financial gets full precision.
  if (abbreviate && Math.abs(pence) >= 100_000_00) {
    const units = pence / 100;
    const [divisor, suffix] =
      Math.abs(units) >= 1_000_000 ? [1_000_000, 'm'] : [1_000, 'k'];
    return `${symbolFor(currency)}${(units / divisor).toFixed(1)}${suffix}`;
  }

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(pence / 100);
}

function symbolFor(currency: string): string {
  return { GBP: '£', EUR: '€', USD: '$' }[currency] ?? `${currency} `;
}

/** Percentage of a of b, for budget utilisation bars. Guards divide-by-zero. */
export function percentOf(part: Money, whole: Money): number {
  const w = toPence(whole);
  if (w === 0) return 0;
  return (toPence(part) / w) * 100;
}
